/**
 * ImportDialog — Data import with validation preview
 *
 * Supports file upload, JSON schema validation (dry-run), scope selection from
 * the available scopes in the file, conflict strategy selection, and the final
 * import with a result summary.
 */

import { useState, useRef } from 'react';
import { useAppDispatch, useAppSelector } from '@/features/hooks';
import {
  validateImport,
  importData,
  clearValidation,
  clearImportResult,
} from '@/features/dataPortability/dataPortabilitySlice';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Upload,
  Loader2,
  FileJson,
  CheckCircle,
  XCircle,
  AlertTriangle,
  ChevronRight,
} from 'lucide-react';
import {
  DataScope,
  ConflictStrategy,
  DATA_SCOPE_LABELS,
  type ExportFile,
  type ImportValidation,
} from '@/lib/types';

type Step = 'upload' | 'configure' | 'validate' | 'result';

interface ImportDialogProps {
  trigger?: React.ReactNode;
  onImportComplete?: () => void;
}

export function ImportDialog({ trigger, onImportComplete }: ImportDialogProps) {
  const dispatch = useAppDispatch();
  const { validation, validateLoading, validateError, importResult, importLoading, importError } =
    useAppSelector((s) => s.dataPortability);
  const [open, setOpen] = useState(false);
  const [step, setStep] = useState<Step>('upload');
  const [file, setFile] = useState<ExportFile | null>(null);
  const [fileName, setFileName] = useState('');
  const [parseError, setParseError] = useState('');
  const [selectedScopes, setSelectedScopes] = useState<DataScope[]>([]);
  const [conflictStrategy, setConflictStrategy] = useState<ConflictStrategy>(ConflictStrategy.SKIP);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const availableScopes: DataScope[] = file
    ? (Object.keys(file.data ?? {}).filter((k) =>
        Object.values(DataScope).includes(k as DataScope)
      ) as DataScope[])
    : [];

  const resetState = () => {
    setStep('upload');
    setFile(null);
    setFileName('');
    setParseError('');
    setSelectedScopes([]);
    setConflictStrategy(ConflictStrategy.SKIP);
    dispatch(clearValidation());
    dispatch(clearImportResult());
  };

  const handleOpenChange = (v: boolean) => {
    setOpen(v);
    if (!v) resetState();
  };

  // ── Step 1: File upload ──────────────────────────────────────────────────

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setParseError('');
    setFileName(f.name);

    const reader = new FileReader();
    reader.onload = (ev) => {
      try {
        const parsed = JSON.parse(ev.target?.result as string) as ExportFile;
        if (!parsed?.metadata?.format || !parsed?.data) {
          setParseError('Invalid FinTrack export file. Missing metadata or data fields.');
          setFile(null);
          return;
        }
        if (parsed.metadata.format !== 'fintrack-export') {
          setParseError(`Unrecognised format: "${parsed.metadata.format}". Expected "fintrack-export".`);
          setFile(null);
          return;
        }
        setFile(parsed);
        // Pre-select all available scopes
        const scopes = Object.keys(parsed.data ?? {}).filter((k) =>
          Object.values(DataScope).includes(k as DataScope)
        ) as DataScope[];
        setSelectedScopes(scopes);
      } catch {
        setParseError('Failed to parse JSON. Please ensure the file is a valid FinTrack export.');
        setFile(null);
      }
    };
    reader.readAsText(f);
  };

  // ── Step 2 → 3: Validate ────────────────────────────────────────────────

  const handleValidate = async () => {
    if (!file || selectedScopes.length === 0) return;
    try {
      await dispatch(validateImport({ file })).unwrap();
      setStep('validate');
    } catch {
      // error shown in component
    }
  };

  // ── Step 3 → 4: Import ───────────────────────────────────────────────────

  const handleImport = async () => {
    if (!file || selectedScopes.length === 0) return;
    try {
      await dispatch(importData({ file, scopes: selectedScopes, conflictStrategy })).unwrap();
      setStep('result');
      onImportComplete?.();
    } catch {
      // error shown in component
    }
  };

  const toggleScope = (scope: DataScope) => {
    setSelectedScopes((prev) =>
      prev.includes(scope) ? prev.filter((s) => s !== scope) : [...prev, scope]
    );
  };

  // ── Render helpers ───────────────────────────────────────────────────────

  const renderUpload = () => (
    <div className="space-y-4">
      <div
        className="border-2 border-dashed rounded-lg p-8 text-center cursor-pointer hover:border-primary/60 transition-colors"
        onClick={() => fileInputRef.current?.click()}
        data-testid="import-dropzone"
      >
        <FileJson className="h-10 w-10 mx-auto mb-3 text-muted-foreground" />
        <p className="text-sm font-medium">
          {fileName || 'Click to select a FinTrack export file'}
        </p>
        <p className="text-xs text-muted-foreground mt-1">.json files only</p>
        <input
          ref={fileInputRef}
          type="file"
          accept="application/json,.json"
          className="hidden"
          onChange={handleFileChange}
          data-testid="import-file-input"
        />
      </div>

      {parseError && (
        <Alert variant="destructive" data-testid="import-parse-error">
          <XCircle className="h-4 w-4" />
          <AlertDescription>{parseError}</AlertDescription>
        </Alert>
      )}

      {file && (
        <div className="rounded-lg border bg-muted/40 p-3 space-y-1 text-sm" data-testid="import-file-summary">
          <p className="font-medium">{fileName}</p>
          <p className="text-muted-foreground">
            Format: <span className="text-foreground">{file.metadata.format} v{file.metadata.schemaVersion}</span>
          </p>
          <p className="text-muted-foreground">
            Exported: <span className="text-foreground">{new Date(file.metadata.exportedAt).toLocaleString()}</span>
          </p>
          <div className="flex flex-wrap gap-1 pt-1">
            {availableScopes.map((s) => (
              <Badge key={s} variant="secondary" className="text-xs capitalize">{s}</Badge>
            ))}
          </div>
        </div>
      )}
    </div>
  );

  const renderConfigure = () => (
    <div className="space-y-4">
      {/* Scope selection */}
      <div className="space-y-3">
        <Label className="text-sm font-medium">Scopes to Import</Label>
        {availableScopes.length === 0 && (
          <p className="text-sm text-muted-foreground">No importable scopes found in file.</p>
        )}
        {availableScopes.map((scope) => (
          <div key={scope} className="flex items-center gap-3">
            <Checkbox
              id={`import-scope-${scope}`}
              checked={selectedScopes.includes(scope)}
              onCheckedChange={() => toggleScope(scope)}
              data-testid={`import-scope-${scope}`}
            />
            <Label htmlFor={`import-scope-${scope}`} className="cursor-pointer capitalize">
              {DATA_SCOPE_LABELS[scope]}
              <span className="ml-2 text-xs text-muted-foreground">
                ({file?.metadata.counts?.[scope] ?? 0} records)
              </span>
            </Label>
          </div>
        ))}
      </div>

      {/* Conflict strategy */}
      <div className="space-y-2 pt-2 border-t">
        <Label className="text-sm font-medium">Conflict Strategy</Label>
        <p className="text-xs text-muted-foreground">
          How to handle records that already exist in your account.
        </p>
        <Select
          value={conflictStrategy}
          onValueChange={(v) => setConflictStrategy(v as ConflictStrategy)}
        >
          <SelectTrigger data-testid="conflict-strategy-select">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value={ConflictStrategy.SKIP}>
              Skip — keep existing, ignore duplicates
            </SelectItem>
            <SelectItem value={ConflictStrategy.OVERWRITE}>
              Overwrite — replace existing records
            </SelectItem>
            <SelectItem value={ConflictStrategy.DUPLICATE}>
              Duplicate — import as new records with "(imported)" suffix
            </SelectItem>
          </SelectContent>
        </Select>
      </div>
    </div>
  );

  const renderValidation = (v: ImportValidation) => (
    <div className="space-y-3" data-testid="import-validation-result">
      {v.errors.length > 0 && (
        <Alert variant="destructive">
          <XCircle className="h-4 w-4" />
          <AlertDescription>
            <ul className="list-disc list-inside space-y-1">
              {v.errors.map((err, i) => <li key={i}>{err}</li>)}
            </ul>
          </AlertDescription>
        </Alert>
      )}
      {v.warnings.length > 0 && (
        <Alert className="border-amber-200 bg-amber-50 dark:bg-amber-950/20 dark:border-amber-800">
          <AlertTriangle className="h-4 w-4 text-amber-600" />
          <AlertDescription className="text-amber-700 dark:text-amber-400">
            {v.warnings.map((w, i) => <div key={i}>{w}</div>)}
          </AlertDescription>
        </Alert>
      )}
      {v.valid && (
        <Alert className="border-green-200 bg-green-50 dark:bg-green-950/20 dark:border-green-800">
          <CheckCircle className="h-4 w-4 text-green-600" />
          <AlertDescription className="text-green-700 dark:text-green-400">
            File is valid — {v.availableScopes.length} scope(s) available to import.
          </AlertDescription>
        </Alert>
      )}

      <div className="space-y-2">
        {v.availableScopes.map((scope) => (
          <div key={scope} className="flex items-center justify-between text-sm rounded-lg border p-2">
            <span className="font-medium capitalize">{DATA_SCOPE_LABELS[scope as DataScope] ?? scope}</span>
            <Badge variant="outline">{v.counts[scope as keyof typeof v.counts] ?? 0} records</Badge>
          </div>
        ))}
      </div>
    </div>
  );

  const renderResult = () => {
    if (!importResult) return null;
    const totalCreated = importResult.scopes.reduce((s, r) => s + r.created, 0);
    const totalSkipped = importResult.scopes.reduce((s, r) => s + r.skipped, 0);
    return (
      <div className="space-y-3" data-testid="import-result">
        <Alert className="border-green-200 bg-green-50 dark:bg-green-950/20 dark:border-green-800">
          <CheckCircle className="h-4 w-4 text-green-600" />
          <AlertDescription className="text-green-700 dark:text-green-400">
            Import complete — {totalCreated} record(s) imported
            {totalSkipped > 0 ? `, ${totalSkipped} skipped` : ''}.
          </AlertDescription>
        </Alert>

        <div className="space-y-2">
          {importResult.scopes.map((s) => (
            <div key={s.scope} className="flex items-center justify-between text-sm rounded-lg border p-2">
              <span className="font-medium capitalize">{DATA_SCOPE_LABELS[s.scope as DataScope] ?? s.scope}</span>
              <div className="flex items-center gap-2">
                {s.created > 0 && <Badge variant="outline" className="bg-green-50 text-green-700 border-green-200">{s.created} created</Badge>}
                {s.overwritten > 0 && <Badge variant="outline" className="bg-blue-50 text-blue-700 border-blue-200">{s.overwritten} updated</Badge>}
                {s.skipped > 0 && <Badge variant="outline">{s.skipped} skipped</Badge>}
                {s.errors.length > 0 && <Badge variant="destructive">{s.errors.length} errors</Badge>}
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  };

  const canGoNext = () => {
    if (step === 'upload') return !!file && !parseError;
    if (step === 'configure') return selectedScopes.length > 0;
    if (step === 'validate') return validation?.valid === true;
    return false;
  };

  const stepLabels: Record<Step, string> = {
    upload: 'Select File',
    configure: 'Configure',
    validate: 'Validate',
    result: 'Done',
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogTrigger asChild>
        {trigger ?? (
          <Button variant="outline" data-testid="import-data-btn">
            <Upload className="h-4 w-4 mr-2" />
            Import Data
          </Button>
        )}
      </DialogTrigger>
      <DialogContent className="max-w-lg" data-testid="import-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Upload className="h-5 w-5 text-primary" />
            Import Data
          </DialogTitle>
          <DialogDescription>
            Import data from a FinTrack export file. Step {Object.keys(stepLabels).indexOf(step) + 1} of 4 —{' '}
            <span className="font-medium">{stepLabels[step]}</span>
          </DialogDescription>
        </DialogHeader>

        <div className="py-2">
          {step === 'upload' && renderUpload()}
          {step === 'configure' && renderConfigure()}
          {step === 'validate' && (
            validateLoading ? (
              <div className="flex items-center justify-center py-8 gap-3">
                <Loader2 className="h-5 w-5 animate-spin text-primary" />
                <span className="text-sm text-muted-foreground">Validating file…</span>
              </div>
            ) : validateError ? (
              <Alert variant="destructive" data-testid="validate-error">
                <XCircle className="h-4 w-4" />
                <AlertDescription>{validateError}</AlertDescription>
              </Alert>
            ) : validation ? (
              renderValidation(validation)
            ) : null
          )}
          {step === 'result' && (
            importLoading ? (
              <div className="flex items-center justify-center py-8 gap-3">
                <Loader2 className="h-5 w-5 animate-spin text-primary" />
                <span className="text-sm text-muted-foreground">Importing data…</span>
              </div>
            ) : importError ? (
              <Alert variant="destructive" data-testid="import-error">
                <XCircle className="h-4 w-4" />
                <AlertDescription>{importError}</AlertDescription>
              </Alert>
            ) : (
              renderResult()
            )
          )}
        </div>

        <DialogFooter className="gap-2">
          {step !== 'result' && (
            <Button
              variant="outline"
              onClick={() => {
                if (step === 'upload') { handleOpenChange(false); return; }
                if (step === 'configure') { setStep('upload'); return; }
                if (step === 'validate') { setStep('configure'); dispatch(clearValidation()); return; }
              }}
            >
              {step === 'upload' ? 'Cancel' : 'Back'}
            </Button>
          )}

          {step === 'upload' && (
            <Button onClick={() => setStep('configure')} disabled={!canGoNext()} data-testid="import-next-btn">
              Configure <ChevronRight className="h-4 w-4 ml-1" />
            </Button>
          )}

          {step === 'configure' && (
            <Button
              onClick={handleValidate}
              disabled={!canGoNext() || validateLoading}
              data-testid="import-validate-btn"
            >
              {validateLoading ? (
                <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Validating…</>
              ) : (
                <>Validate <ChevronRight className="h-4 w-4 ml-1" /></>
              )}
            </Button>
          )}

          {step === 'validate' && (
            <Button
              onClick={handleImport}
              disabled={!canGoNext() || importLoading}
              data-testid="import-confirm-btn"
            >
              {importLoading ? (
                <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Importing…</>
              ) : (
                <>Import Data</>
              )}
            </Button>
          )}

          {step === 'result' && (
            <Button onClick={() => handleOpenChange(false)} data-testid="import-done-btn">
              Done
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
